import {MouseEvent, DetailsHTMLAttributes, ReactNode, useCallback, useContext, useMemo} from 'react';
import {ListItem} from '../../layout';
import {join, tryCall} from '../../utils';
import {Checkbox} from '../checkbox/checkbox';
import {Selectors} from '../controls.selectors';
import SelectableListContext from './selectable-list-context';
import {CheckboxPlacement} from './types';

import style from './selectable-list.module.less';

type BaseDivAttributes = DetailsHTMLAttributes<HTMLDivElement>;

export type SelectableListItemProps<T> = {
	value: T;
	id?: string;
	checkboxPlacement?: CheckboxPlacement;
	disabled?: boolean;
	checked?: boolean;
	children?: ReactNode;
} & BaseDivAttributes;

export function SelectableListItem<T>(props: SelectableListItemProps<T>): JSX.Element {
	const context = useContext(SelectableListContext);
	const checked = useMemo(() => props.checked ?? context?.value?.some(v => v === props.value), [context?.value, props.value, props.checked]);

	const onClick = useCallback((e: MouseEvent<HTMLDivElement>) => {
		tryCall(props.onClick, e);
		if (!(e.isPropagationStopped() || e.isDefaultPrevented())) {
			e.preventDefault();
			e.stopPropagation();
			if (!props.disabled) {
				context.toggleValue(props.value);
			}
		}
	}, [context, props.onClick, props.disabled, props.value]);

	const checkboxPlacement = useMemo(() => props.checkboxPlacement || context.checkboxPlacement, [props.checkboxPlacement, context.checkboxPlacement]);

	const checkbox = <Checkbox checked={checked} disabled={props.disabled}/>;

	return <ListItem data-cy={Selectors.SelectableListItem}
	                 {...props}
	                 id={props.id}
	                 onClick={onClick}
	                 tabIndex={props.tabIndex || 0}
	                 className={join(style.selectableItem, props.className, props.disabled && style.disabled, checkboxPlacement === 'right' && style.spaceBetween)}>
		{checkboxPlacement === 'left' && checkbox}
		{props.children}
		{checkboxPlacement === 'right' && checkbox}
	</ListItem>;
}
